(function () {
  'use strict';

  function init() {
    const selectors = document.querySelectorAll('#hcc-mod-selector');
    if (!selectors.length) return;

    const productForm = Array.from(selectors)
      .map((selector) => selector.closest('product-form-component')?.querySelector('form'))
      .find(Boolean);

    if (!productForm) {
      console.warn('[HCC Modification] Product form was not found.');
      return;
    }

    window.addEventListener('submit', handleSubmit, true);
    window.addEventListener('change', handleSelectionChange, true);

    selectors.forEach(updateAcceleratedCheckoutState);
  }

  async function handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const selector = form.querySelector('#hcc-mod-selector');
    if (!selector) return;

    const selected = selector.querySelector('input[name="hcc_garment_modification"]:checked');
    if (!selected || selected.value === 'none') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const shirtVariantId = Number(form.querySelector('input[name="id"]')?.value);
    const serviceVariantId = Number(selected.dataset.serviceVariantId);
    const quantity = getQuantity(form);
    const modification = selected.dataset.modLabel || '';
    const bundleId = `hcc-mod-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    if (!shirtVariantId || !serviceVariantId || !modification) {
      console.error('[HCC Modification] Missing product or service variant information.');
      return;
    }

    setLoading(form, true);

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: shirtVariantId,
              quantity: quantity,
              properties: {
                'Selected modification': modification,
                '_hcc_mod_bundle': bundleId,
                '_hcc_mod_role': 'garment',
                '_hcc_mod_service_variant_id': String(serviceVariantId),
              },
            },
            {
              id: serviceVariantId,
              quantity: quantity,
              properties: {
                Modification: modification,
                'Modification for': selector.dataset.productTitle || '',
                'Parent variant ID': String(shirtVariantId),
                'Parent product handle': selector.dataset.productHandle || '',
                '_hcc_mod_bundle': bundleId,
                '_hcc_mod_role': 'service',
              },
            },
          ],
          sections: getCartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw data;
      }

      const cart = await fetchCart();
      const verified = hasLinkedModificationPair(cart, bundleId, shirtVariantId, serviceVariantId, modification, quantity);

      if (!verified) {
        await removeBundleFromCart(cart, bundleId);
        throw new Error('The modification charge could not be added, so the modified item was removed. Please refresh and try again.');
      }

      dispatchCartUpdate(cart, data.sections);
      openCartDrawer();
    } catch (error) {
      console.error('[HCC Modification] Cart add failed:', error);
      alert(error?.description || error?.message || 'Sorry, that could not be added to your cart. Please try again.');
    } finally {
      setLoading(form, false);
    }
  }

  function getQuantity(form) {
    const quantitySelector = form.closest('product-form-component')?.querySelector('quantity-selector-component');
    const componentValue = quantitySelector?.getValue?.();
    if (componentValue) return Number(componentValue);

    const quantityInput = form.querySelector('input[name="quantity"]');
    return Number(quantityInput?.value) || 1;
  }

  function handleSelectionChange(event) {
    const selector = event.target?.closest?.('#hcc-mod-selector');
    if (!selector) return;

    updateAcceleratedCheckoutState(selector);
  }

  function updateAcceleratedCheckoutState(selector) {
    const formComponent = selector.closest('product-form-component');
    const acceleratedCheckout = formComponent?.querySelector('.accelerated-checkout-block');
    if (!acceleratedCheckout) return;

    const selected = selector.querySelector('input[name="hcc_garment_modification"]:checked');
    const shouldDisable = Boolean(selected && selected.value !== 'none');

    acceleratedCheckout.hidden = shouldDisable;
    acceleratedCheckout.setAttribute('aria-hidden', String(shouldDisable));
  }

  function hasLinkedModificationPair(cart, bundleId, shirtVariantId, serviceVariantId, modification, quantity) {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    const hasGarment = items.some((item) => {
      const properties = item.properties || {};
      return (
        Number(item.id) === Number(shirtVariantId) &&
        Number(item.quantity) >= Number(quantity) &&
        properties['Selected modification'] === modification &&
        properties['_hcc_mod_bundle'] === bundleId &&
        properties['_hcc_mod_role'] === 'garment'
      );
    });

    const hasService = items.some((item) => {
      const properties = item.properties || {};
      return (
        Number(item.id) === Number(serviceVariantId) &&
        Number(item.quantity) >= Number(quantity) &&
        properties['_hcc_mod_bundle'] === bundleId &&
        properties['_hcc_mod_role'] === 'service'
      );
    });

    return hasGarment && hasService;
  }

  async function removeBundleFromCart(cart, bundleId) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const linkedItems = items.filter((item) => item.properties?.['_hcc_mod_bundle'] === bundleId);

    await Promise.all(
      linkedItems.map((item) =>
        fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: item.key,
            quantity: 0,
          }),
        })
      )
    );
  }

  function getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return response.json();
  }

  function dispatchCartUpdate(cart, sections) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'hcc-product-modification',
          data: {
            source: 'hcc-product-modification',
            itemCount: cart?.item_count,
            sections: sections || {},
          },
        },
      })
    );
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer?.open) {
      drawer.open();
    } else if (drawer?.showDialog) {
      drawer.showDialog();
    }
  }

  function setLoading(form, isLoading) {
    const button = form.querySelector('button[type="submit"][name="add"]');
    if (!button) return;

    button.disabled = isLoading;
    button.setAttribute('aria-disabled', String(isLoading));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
